import React, { useCallback, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router-dom';
import { Box, Card, Grid } from '@mui/material';
import { FormikValues, useFormik } from 'formik';

import {
  doesEmailExist,
  emailExists,
  getLanguages,
  getSignupRequestId,
  initiateSignup,
  preLoginLang,
  resetApproval,
  setSignedUp,
  setUserProps,
  SocialLoginType,
  username,
} from '@pharmaplan/common';
import { encryptPassword } from '@pharmaplan/common/helpers/utils';
import { useAppSelector } from '../../hooks/useAppSelector';
import classes from './styles';
import strings from '../../localization';
import { ReactComponent as LoginImageOne } from '../../assets/svg/loginImg2.svg';
import { ReactComponent as LoginImageTwo } from '../../assets/svg/loginImg1.svg';

import { ReactComponent as LoginImageLeft1 } from '../../assets/img/self/loginImgLeft1.svg';
import { ReactComponent as LoginImageLeft2 } from '../../assets/img/self/loginImgLeft2.svg';
import { ReactComponent as LoginImageRight1 } from '../../assets/img/self/loginImgRight1.svg';
import { ReactComponent as LoginImageRight2 } from '../../assets/img/self/loginImgRight2.svg';

import { ILoginLayout } from '../../types/ILoginLayout';
import LoginSocial from './LoginSocial/LoginSocial';
import Validator from '../Signup/SignupForm/Validator';
import { resetAll } from '../../actions/persistActions';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { Constants } from '../../helpers/Constants';
import Footer from '../Footer';
import CustomLink from '../common/CustomLink';
import {
  getBrowser,
  getPlatformOS,
  setPageTitle,
} from '../../helpers/Functions';
import useLanguage from '../../hooks/useLanguge';
import useNavAfterLogin from '../../hooks/useNavAfterLogin';
import useSelfService from '../../hooks/useSelfService';
import useCheckMobile from '../../hooks/useCheckMobile';
import useRedirectStore from '../../hooks/useRedirectStore';
import useAnalytics from '../../analytics/useAnalytics';
import usePromoToken from '../../hooks/Signup/usePromoToken';

const {
  mainContainer,
  gridContainer,
  outerContainer,
  signupMargin,
  socialContainer,
  loginCard,
  loginContainer,
  loginImageContainer,
  loginImageOne,
  loginImageLeft,
  loginImageTwo,
  rememberMeNoAccount,
  rememberMeSignup,
  responsive,
} = classes;

const {
  paths: { signup: signupPath, signupFlow },

  appName,
} = Constants;

const LoginLayout = ({ Header, Form, isSignup }: ILoginLayout) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();
  const analytics = useAnalytics();

  const { changeSignupLang } = useLanguage();
  const isMounted = useRef(false);

  const userRequestId = useAppSelector(getSignupRequestId);
  const { token: promoToken } = usePromoToken();
  const languages = useAppSelector(getLanguages);
  const name = useAppSelector(username);

  const preLoginLanguage = useAppSelector(preLoginLang);
  const isEmailExist = useAppSelector(emailExists);

  useNavAfterLogin();

  const setUserPreferredLang = (language: string) => {
    const filterLanguage = languages.find(
      ({ languageId }) =>
        languageId === language,
    );

    const { code } = filterLanguage ?? {};

    if (preLoginLanguage !== code) {
      changeSignupLang(code ?? preLoginLanguage);
    }
  };

  const handleSubmit = (values: FormikValues) => {
    const { email } = values ?? {};
    dispatch(doesEmailExist(email));
    dispatch(initiateSignup(email));
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      role: '',
      language: '',
      token: '',
      loginType: SocialLoginType.google,
      isMediaLogin: false,
    },

    onSubmit: handleSubmit,
    validationSchema: Validator,
    enableReinitialize: true,
  });

  const { values } = formik ?? {};
  const { email, password, role, language, isMediaLogin, token, loginType } = values ?? {};

  const handleLogins = () => {
    dispatch(resetAll());
    setUserPreferredLang(language);
    dispatch(
      setUserProps({
        promoToken,
        email,
        password: isMediaLogin ? null : encryptPassword(password),
        userType: role,
        appid: 'pp-windows',
        token: isMediaLogin ? token : null,
        loginType: isMediaLogin ? loginType : null,
        language,
        device: {
          osType: getPlatformOS() ?? '',
          type: getBrowser(),
          deviceToken: null,
        },
      }),
    );
    analytics?.setUser(userRequestId ?? '');
    dispatch(setSignedUp(true));
    dispatch(resetApproval());
    navigate(signupFlow);
  };

  useEffect(() => {
    setPageTitle(`${strings.welcomeTo} ${appName}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else if (isEmailExist === false && userRequestId) {
      handleLogins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmailExist, userRequestId]);

  const LoginIcons = useCallback(() => {
    if (isSelfService) {
      return (
        <>
          <Box sx={loginImageLeft}>
            <LoginImageLeft2 />
            <LoginImageLeft1 />
          </Box>
          <Box sx={loginImageTwo}>
            <LoginImageRight1 />
            <LoginImageRight2 />
          </Box>
        </>
      );
    }
    return (
      <>
        <LoginImageOne style={loginImageOne} />
        <LoginImageTwo style={loginImageTwo} />
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRedirectStore();
  const { isMobileSize } = useCheckMobile();

  if (isMobileSize) {
    return null;
  }

  return (
    <Box component="div" sx={mainContainer}>
      <Grid container sx={gridContainer}>
        <Grid item>
          <Box sx={[outerContainer, signupMargin]}>
            <Card sx={loginCard}>
              <Header />
              <Box component="div">
                <Box sx={loginContainer}>
                  <Form formik={formik} />
                </Box>
                {!name || isSignup ? (
                  <Box sx={socialContainer}>
                    <LoginSocial formik={formik} isSignup={isSignup ?? false} />
                  </Box>
                ) : (
                  <Box sx={rememberMeSignup}>
                    <Box component="div" sx={rememberMeNoAccount}>
                      {strings.noAccount}
                    </Box>
                    <CustomLink linkText={strings.signup} to={signupPath} />
                  </Box>
                )}
              </Box>
            </Card>

            {/* Bottom SVGs of Card */}
            <Box sx={[loginImageContainer, responsive]}>
              <LoginIcons />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Footer />
    </Box>
  );
};

export default LoginLayout;
